"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { usePathname } from "next/navigation";

// Site-wide popup promoting the swipe deck (replaces the old email-signup
// popup). Auto-opens once per visitor a few seconds after load; dismissal is
// persisted in localStorage. Copy is editable in Sanity (siteSettings →
// "Swipe Deck Popup"); the defaults below mirror the original modal.
const DISMISSED_STORAGE_KEY = "vault88-swipe-promo-dismissed";
const OPEN_DELAY_MS = 3000;

type Props = {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  ctaText?: string;
  dismissText?: string;
};

export function SwipeDeckPromoModal({
  eyebrow = "✨ New Feature ✨",
  title = "Swipe through the Vault 88 collection",
  subtitle = "(in a very familiar way)",
  body = "We’ll look at your likes, dislikes and recommend pieces to suit your individual taste and style.",
  ctaText = "START SWIPING!",
  dismissText = "back to classic browsing",
}: Props) {
  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Never promote the swipe deck to visitors who are already on it.
  const pathname = usePathname();
  const onSwipePage = pathname?.startsWith("/browse/swipe") ?? false;

  useEffect(() => {
    setMounted(true);
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(DISMISSED_STORAGE_KEY) === "1";
    } catch {
      // storage blocked — show the popup anyway.
    }
    if (!dismissed) {
      openTimeoutRef.current = setTimeout(() => setIsOpen(true), OPEN_DELAY_MS);
    }
    return () => {
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isOpen || onSwipePage) return;
    document.documentElement.classList.add("scroll-lock");
    document.body.classList.add("scroll-lock");
    return () => {
      document.documentElement.classList.remove("scroll-lock");
      document.body.classList.remove("scroll-lock");
    };
  }, [isOpen, onSwipePage]);

  const dismiss = () => {
    setIsOpen(false);
    try {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, "1");
    } catch {
      // ignore
    }
  };

  if (!mounted || !isOpen || onSwipePage) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Close swipe deck prompt"
        className="absolute inset-0 bg-black/40"
        onClick={dismiss}
      />
      <div className="relative w-full max-w-md rounded-sm border border-slate-200 bg-white p-6 text-center shadow-soft">
        {eyebrow ? (
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{eyebrow}</p>
        ) : null}
        <h2 className="mt-3 text-2xl font-semibold text-slate-900 font-[var(--font-display)]">
          {title}
        </h2>
        {subtitle ? <p className="mt-3 text-sm text-slate-600">{subtitle}</p> : null}
        {body ? <p className="mt-4 text-sm text-slate-600">{body}</p> : null}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Link
            href="/browse/swipe"
            onClick={dismiss}
            className="uppercase inline-flex items-center justify-center rounded-sm bg-[var(--primary)] px-5 py-3 text-xs font-semibold tracking-[0.3em] text-white shadow-soft transition hover:-translate-y-0.5"
          >
            {ctaText}
          </Link>
          <button
            type="button"
            onClick={dismiss}
            className="uppercase inline-flex items-center justify-center rounded-sm border border-slate-300 px-5 py-3 text-xs font-semibold tracking-[0.3em] text-slate-700 transition hover:border-slate-500 hover:text-slate-900"
          >
            {dismissText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
